import { errorMessageContentInvalid, errorMessageContentMinMax, errorMessageContentNumberOnly } from '~/consts/zipcode';
import { ZipCodeRange, ZipCodeRangeForService } from '~/definitions/zipcode';

export class ZipCodeParser {
    private static readonly MIN_ZIP_CODE = 0;
    private static readonly MAX_ZIP_CODE = 9999;

    /**
     * Validates if a zip code is within the allowed range (0000-9999)
     */
    private static isValidZipCode(zipCode: number): boolean {
        return zipCode >= this.MIN_ZIP_CODE && zipCode <= this.MAX_ZIP_CODE;
    }

    public static splitLargeRange(rangeStr: string): string[] {
        const [startStr, endStr] = rangeStr.split('-').map((s) => s.trim());

        const start = Number(startStr);
        const end = Number(endStr);

        // Get how many digits the original numbers had (e.g., "0000" → 4)
        const padLength = startStr.length;

        if (isNaN(start) || isNaN(end) || start >= end) {
            // Invalid range or single number — return as is
            return [rangeStr];
        }

        const result: string[] = [];
        let currentStart = start;

        while (currentStart <= end) {
            const currentEnd = Math.min(currentStart + 99, end);

            // Format numbers with leading zeros
            const formattedStart = String(currentStart).padStart(padLength, '0');
            const formattedEnd = String(currentEnd).padStart(padLength, '0');

            if (currentStart === currentEnd) {
                result.push(formattedStart);
            } else {
                result.push(`${formattedStart}-${formattedEnd}`);
            }

            currentStart = currentEnd + 1;
        }

        return result;
    }

    public static validation(zipCodesToAdd: ZipCodeRange): {
        [key in keyof ZipCodeRange]?: string;
    } {
        const startStr = zipCodesToAdd.start;
        const endStr = zipCodesToAdd.end;

        const errors: { [key in keyof ZipCodeRange]?: string } = {};

        // --- Validate start ---
        const startNum = parseInt(startStr ?? '', 10);

        const isValid4Digit = (value: string | number): boolean => {
            const str = value?.toString();
            return /^\d{4}$/.test(str);
        };

        if (isNaN(startNum)) {
            errors.start = errorMessageContentNumberOnly;
        } else if (!startStr || !isValid4Digit(startStr)) {
            errors.start = errorMessageContentInvalid;
        } else if (!this.isValidZipCode(startNum)) {
            errors.start = errorMessageContentInvalid;
        }

        // --- Validate end (only if provided) ---
        if (endStr) {
            const endNum = parseInt(endStr, 10);

            if (isNaN(endNum)) {
                errors.end = errorMessageContentNumberOnly;
            } else if (!isValid4Digit(endStr)) {
                errors.end = errorMessageContentInvalid;
            } else if (!this.isValidZipCode(endNum)) {
                errors.end = errorMessageContentInvalid;
            }

            // --- Validate range: start <= end ---
            if (!errors.start && !errors.end && !isNaN(startNum) && !isNaN(endNum) && startNum > endNum) {
                errors.start = errorMessageContentMinMax;
                errors.end = errorMessageContentMinMax;
            }
        }

        return errors;
    }

    /**
     * Parses a zip code string into an array of ranges
     */
    public static parse(input: string): ZipCodeRangeForService[] {
        if (!input || typeof input !== 'string') {
            return [];
        }

        // Remove all spaces and split by comma
        const parts = input
            .replace(/\s+/g, '')
            .split(',')
            .filter((part) => part.trim() !== '');
        const ranges: ZipCodeRangeForService[] = [];

        for (const part of parts) {
            if (part.includes('-') && part.split('-').length === 2) {
                // Handle range (e.g., "2001-2010")
                const [startStr, endStr] = part.split('-');

                // Check if both parts are valid numbers (not empty strings)
                if (startStr === '' || endStr === '') {
                    throw new Error(`Invalid zip code: ${part}`);
                }

                const start = parseInt(startStr, 10);
                const end = parseInt(endStr, 10);

                if (isNaN(start) || isNaN(end)) {
                    throw new Error(`Invalid zip code: ${part}`);
                }

                if (!this.isValidZipCode(start) || !this.isValidZipCode(end)) {
                    throw new Error(`Invalid zip code range: ${part}`);
                }

                if (start > end) {
                    throw new Error(`Invalid range: start (${start}) cannot be greater than end (${end})`);
                }

                ranges.push({ start, end });
            } else {
                // Handle single zip code
                const zipCode = parseInt(part, 10);

                if (isNaN(zipCode) || !this.isValidZipCode(zipCode)) {
                    throw new Error(`Invalid zip code: ${part}`);
                }

                ranges.push({ start: zipCode, end: zipCode });
            }
        }

        return this.mergeRanges(ranges);
    }

    /**
     * Adds zip codes to the existing parsed ranges
     */
    public static addZipCodes(
        existingRanges: ZipCodeRangeForService[],
        zipCodesToAdd: string
    ): ZipCodeRangeForService[] {
        const newRanges = this.parse(zipCodesToAdd);
        const allRanges = [...existingRanges, ...newRanges];
        return this.mergeRanges(allRanges);
    }

    /**
     * Removes zip codes from the existing parsed ranges
     */
    public static removeZipCodes(
        existingRanges: ZipCodeRangeForService[],
        zipCodesToRemove: string
    ): ZipCodeRangeForService[] {
        const rangesToRemove = this.parse(zipCodesToRemove);
        let result = [...existingRanges];

        for (const removeRange of rangesToRemove) {
            result = this.removeRangeFromRanges(result, removeRange);
        }

        return result;
    }

    /**
     * Converts ranges back to string format
     */
    public static stringify(ranges: ZipCodeRangeForService[]): string {
        return ranges
            .map((range) => {
                const startStr = range.start.toString().padStart(4, '0');
                const endStr = range.end?.toString().padStart(4, '0');

                if (!range.end || range.start === range.end) {
                    return startStr;
                } else {
                    return `${startStr}-${endStr}`;
                }
            })
            .join(',');
    }

    /**
     * Convenience method to parse input string and return formatted string
     */
    public static parseAndStringify(input: string): string {
        const ranges = this.parse(input);
        return this.stringify(ranges);
    }

    /**
     * Merges overlapping or adjacent ranges and sorts them
     */
    private static mergeRanges(ranges: ZipCodeRangeForService[]): ZipCodeRangeForService[] {
        if (ranges.length === 0) return [];

        // Sort ranges by start value
        const sorted = ranges.sort((a, b) => a.start - b.start);
        const merged: ZipCodeRangeForService[] = [sorted[0]];

        for (let i = 1; i < sorted.length; i++) {
            const current = sorted[i];
            const last = merged[merged.length - 1];

            // Check if ranges overlap or are adjacent
            if (current.start <= last.end + 1) {
                // Merge ranges
                last.end = Math.max(last.end, current.end);
            } else {
                // Add new range
                merged.push(current);
            }
        }

        return merged;
    }

    /**
     * Removes a specific range from an array of ranges
     */
    private static removeRangeFromRanges(
        ranges: ZipCodeRangeForService[],
        rangeToRemove: ZipCodeRangeForService
    ): ZipCodeRangeForService[] {
        const result: ZipCodeRangeForService[] = [];

        for (const range of ranges) {
            const fragments = this.removeRangeFromSingleRange(range, rangeToRemove);
            result.push(...fragments);
        }

        return result;
    }

    /**
     * Removes a range from a single range, potentially splitting it into fragments
     */
    private static removeRangeFromSingleRange(
        range: ZipCodeRangeForService,
        rangeToRemove: ZipCodeRangeForService
    ): ZipCodeRangeForService[] {
        // No overlap
        if (rangeToRemove.end < range.start || rangeToRemove.start > range.end) {
            return [range];
        }

        const result: ZipCodeRangeForService[] = [];

        // Left fragment (before the removed range)
        if (range.start < rangeToRemove.start) {
            result.push({ start: range.start, end: rangeToRemove.start - 1 });
        }

        // Right fragment (after the removed range)
        if (range.end > rangeToRemove.end) {
            result.push({ start: rangeToRemove.end + 1, end: range.end });
        }

        return result;
    }

    // Helper: parse a single range or list string like "2001-2010,2015,2017" into a Set of numbers
    public static parseZipcodeStringToSet(zipcodeStr: string): Set<number> {
        const parts = zipcodeStr.split(',').map((s) => s.trim());
        const zipSet = new Set<number>();

        for (const part of parts) {
            if (part.includes('-')) {
                const [startStr, endStr] = part.split('-').map((s) => s.trim());
                const start = Number(startStr);
                const end = Number(endStr);
                if (isNaN(start) || isNaN(end) || start > end) continue;

                for (let i = start; i <= end; i++) {
                    zipSet.add(i);
                }
            } else {
                const num = Number(part);
                if (!isNaN(num)) {
                    zipSet.add(num);
                }
            }
        }

        return zipSet;
    }

    // Parse input range string like "2001-2002" to an array of numbers
    public static parseRangeToArray(rangeStr: string): number[] {
        const [startStr, endStr] = rangeStr.split('-').map((s) => s.trim());
        const start = Number(startStr);
        const end = endStr ? Number(endStr) : start;

        if (isNaN(start) || isNaN(end) || end < start) return [];

        const result = [];
        for (let i = start; i <= end; i++) {
            result.push(i);
        }
        return result;
    }

    // Main function: check duplicated zipcode
    public static findDuplicatedZipcode(inputRange: string, existingZipcodeArrays: string[]): number[] {
        const existingZipSet = new Set<number>();
        for (const zipcodeStr of existingZipcodeArrays) {
            const setFromStr = this.parseZipcodeStringToSet(zipcodeStr);
            for (const z of Array.from(setFromStr)) {
                existingZipSet.add(z);
            }
        }

        const inputZipcode = this.parseRangeToArray(inputRange);

        const duplicates = inputZipcode.filter((z) => existingZipSet.has(z));

        return duplicates;
    }

    /**
     * Finds the segment (single zip or range) in the merged string that contains the given zip code.
     * Returns the segment string if found, otherwise returns an empty string.
     */
    public static findZipcodeSegment(zip: string, merged: string): string {
        const found = merged.split(',').find((segment) => {
            if (segment.includes('-')) {
                // Range: e.g. "4000-4008"
                const [start, end] = segment.split('-');
                return zip >= start && zip <= end;
            }
            // Single: e.g. "3000"
            return segment === zip;
        });
        return found ?? '';
    }

    /**
     * Finds the closest previous segment (single or range) before the given zip
     * Used for pagination after deletion
     */
    public static findPreviousZipcodeSegment(zip: string, merged: string): string {
        // Support multiple zip codes as input (comma-separated string)
        const zipList = zip
            .split(',')
            .map((z) => z.trim())
            .filter((z) => z !== '');
        // Use the highest zip code for previous segment logic
        const zipNums = zipList.map((z) => Number(z)).filter((n) => !isNaN(n));
        if (zipNums.length === 0) return '';
        const maxZipNum = Math.max(...zipNums);
        const segments = merged.split(',');
        let closest: string = '';
        let closestEnd: number = -1;
        for (const segment of segments) {
            if (segment.includes('-')) {
                const [start, end] = segment.split('-');
                const startNum = Number(start);
                const endNum = Number(end);
                if (isNaN(startNum) || isNaN(endNum)) continue;
                if (endNum < maxZipNum && endNum > closestEnd) {
                    closest = segment;
                    closestEnd = endNum;
                }
            } else {
                const num = Number(segment);
                if (isNaN(num)) continue;
                if (num < maxZipNum && num > closestEnd) {
                    closest = segment;
                    closestEnd = num;
                }
            }
        }
        return closest;
    }

    public static getExpandedRanges(zipcode: string) {
        const formattedZipcode = ZipCodeParser.parseAndStringify(zipcode)
            .split(',')
            .map((s) => s.trim());
        const expandedRanges = formattedZipcode.flatMap(ZipCodeParser.splitLargeRange);
        const expandedRangesStr = expandedRanges.join(',');

        return {
            expandedRanges,
            expandedRangesStr,
        };
    }
}
